import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import * as React from 'react'

import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'

import type { NonFsdsGeneralV1TestimonialItem } from '@/types/non-fsds-general-v1.type'

interface CommunityFeedbackSectionProps {
  enabled: boolean
  eyebrow?: string
  title: string
  testimonials: NonFsdsGeneralV1TestimonialItem[]
  autoPlay?: boolean
  autoPlayDelayMs?: number
  loop?: boolean
  pauseOnHover?: boolean
  stopOnInteraction?: boolean
  showArrows?: boolean
  desktopColumns?: 1 | 2 | 3
  backgroundColor?: string
  accentColor?: string
}

/** Inline testimonial carousel shared by the public homepage and Puck preview. */
export default function CommunityFeedbackSection({
  enabled,
  eyebrow = 'Learner voices',
  title,
  testimonials,
  autoPlay = true,
  autoPlayDelayMs = 5000,
  loop = true,
  pauseOnHover = true,
  stopOnInteraction = false,
  showArrows = true,
  desktopColumns = 3,
  backgroundColor = '#021127',
  accentColor = '#0A6CF5',
}: CommunityFeedbackSectionProps) {
  const autoplayPlugin = React.useMemo(
    () => Autoplay({ delay: autoPlayDelayMs, stopOnInteraction, stopOnMouseEnter: pauseOnHover }),
    [autoPlayDelayMs, pauseOnHover, stopOnInteraction]
  )

  React.useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      autoplayPlugin.stop()
    }
  }, [autoplayPlugin])

  const carouselTestimonials = React.useMemo(() => {
    if (!loop || testimonials.length < 2) return testimonials

    return Array.from(
      { length: Math.max(6, testimonials.length * 2) },
      (_, index) => testimonials[index % testimonials.length]
    )
  }, [loop, testimonials])

  const desktopBasisClass = {
    1: 'lg:basis-full',
    2: 'lg:basis-1/2',
    3: 'lg:basis-1/3',
  }[desktopColumns]

  if (!enabled) return null

  return (
    <section
      data-testid="gv1-community-feedback-section"
      aria-labelledby="gv1-community-feedback-title"
      className="relative isolate overflow-hidden bg-[var(--gv1-bg-primary)] px-4 py-20 sm:px-6 sm:py-24 lg:py-28"
      style={{ backgroundColor }}
    >
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-80"
        style={{
          background:
            'radial-gradient(circle at 14% 8%, rgba(89,155,255,.2), transparent 34%), radial-gradient(circle at 88% 86%, rgba(62,207,171,.12), transparent 30%)',
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
        aria-hidden="true"
      />

      <div className="gv1-container">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: accentColor }}>
            {eyebrow}
          </p>
          <h2
            id="gv1-community-feedback-title"
            className="mt-4 text-4xl font-bold leading-tight tracking-[-0.025em] text-white sm:text-5xl"
            style={{ fontFamily: 'var(--gv1-font-heading)' }}
          >
            {title || 'What Our Community Says!'}
          </h2>
        </div>

        {testimonials.length === 0 ? (
          <p className="mt-12 text-base text-white/65">Community stories will appear here soon.</p>
        ) : (
          <Carousel
            aria-label={title || 'Community testimonials'}
            className="mt-12 px-1 md:px-12"
            opts={{ align: 'start', loop: loop && carouselTestimonials.length > 1 }}
            plugins={autoPlay && carouselTestimonials.length > 1 ? [autoplayPlugin] : []}
            onMouseEnter={autoPlay && pauseOnHover ? autoplayPlugin.stop : undefined}
            onMouseLeave={autoPlay && pauseOnHover ? autoplayPlugin.reset : undefined}
            onFocus={autoPlay ? autoplayPlugin.stop : undefined}
            onBlur={autoPlay ? autoplayPlugin.reset : undefined}
          >
            <CarouselContent className="-ml-5">
              {carouselTestimonials.map((testimonial, index) => {
                const isDuplicate = index >= testimonials.length

                return (
                  <CarouselItem
                    key={`${testimonial.id || 'testimonial'}-${index}`}
                    className={`basis-[88%] pl-5 sm:basis-[72%] md:basis-1/2 ${desktopBasisClass}`}
                    aria-hidden={isDuplicate || undefined}
                  >
                    <article className="flex h-full min-h-[310px] flex-col rounded-[28px] border border-white/10 bg-white/[0.08] p-7 shadow-[inset_0_1px_0_rgba(255,255,255,.08)] backdrop-blur-sm sm:p-8">
                      <QuoteIcon color={accentColor} />
                      <blockquote className="mt-8 flex-1 text-lg font-medium leading-8 text-white">
                        “{testimonial.quote}”
                      </blockquote>
                      <div className="mt-8 flex items-center gap-4 border-t border-white/10 pt-6">
                        {testimonial.avatar ? (
                          <Image
                            src={testimonial.avatar}
                            alt=""
                            width={52}
                            height={52}
                            className="size-[52px] rounded-full object-cover ring-2 ring-white/15"
                          />
                        ) : (
                          <span className="flex size-[52px] items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white">
                            {testimonial.name?.slice(0, 1) || '?'}
                          </span>
                        )}
                        <div>
                          <p className="font-semibold text-white">{testimonial.name}</p>
                          {testimonial.role && <p className="mt-1 text-sm text-white/60">{testimonial.role}</p>}
                        </div>
                      </div>
                    </article>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
            {showArrows && carouselTestimonials.length > 1 && (
              <>
                <CarouselPrevious className="left-0 hidden size-11 border-white/15 bg-white/10 text-white hover:bg-white/20 hover:text-white md:inline-flex" />
                <CarouselNext className="right-0 hidden size-11 border-white/15 bg-white/10 text-white hover:bg-white/20 hover:text-white md:inline-flex" />
              </>
            )}
          </Carousel>
        )}
      </div>
    </section>
  )
}

function QuoteIcon({ color }: { color: string }) {
  return (
    <svg className="size-10" style={{ color }} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path
        d="M7 23.5C7 15.9 10.7 10.4 18.1 7l1.8 3.4c-4.3 2.2-6.8 5.1-7.4 8.8h5.8V33H7V23.5Zm16 0C23 15.9 26.7 10.4 34.1 7l1.8 3.4c-4.3 2.2-6.8 5.1-7.4 8.8h5.8V33H23V23.5Z"
        fill="currentColor"
      />
    </svg>
  )
}
